import AsyncStorage from '@react-native-async-storage/async-storage';
import apiClient from './api-client';
import {APP_HEADERS} from '../config/appHeaders';
import {BASE_API} from '../config/apis';
import {getCityCoordinates, reverseGeocodeDetails} from './city-service';

const ONBOARDING_CONFIG_URL = `${BASE_API}onboarding/config`;

const TIMEOUT_MS = 8000;

// STOPGAP: user/get-profile does not return the saved answers (answers: null),
// so a copy is kept on this phone. Remove once the backend returns them.
const answersKey = userId => `onboardingAnswers_${userId}`;

// The answers saved on this phone for the logged-in user, or null.
export const getSavedOnboardingAnswers = async () => {
  try {
    const userData = await AsyncStorage.getItem('Data');
    const userId = JSON.parse(userData)?.userId;
    const raw = await AsyncStorage.getItem(answersKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Keeps a copy of the answers on this phone (same shape Profile reads).
const cacheAnswers = async ({
  frequency,
  fuelType,
  multipleVehicles,
  location,
}) => {
  try {
    const userData = await AsyncStorage.getItem('Data');
    const userId = JSON.parse(userData)?.userId;
    const body = {
      answer_1: frequency,
      answer_2: fuelType,
      answer_3: multipleVehicles ? 'yes' : 'no',
      answer_4: location?.mode || '',
      answer_5:
        location?.mode === 'gps'
          ? `${location.latitude},${location.longitude}`
          : location?.city || '',
    };
    await AsyncStorage.setItem(answersKey(userId), JSON.stringify(body));
  } catch (e) {}
};

// GET /v1/onboarding/config  (public endpoint, no auth token needed)
// Returns the "Data" object from the response.
export const getOnboardingConfig = async (language = 'en') => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(ONBOARDING_CONFIG_URL, {
      headers: {
        Accept: 'application/json',
        'Accept-Language': language,
        ...APP_HEADERS,
      },
      signal: controller.signal,
    });
    const json = await res.json();

    if (!res.ok || json?.Success !== 1 || !json?.Data) {
      throw new Error(json?.Message || 'Failed to load onboarding config');
    }
    return json.Data;
  } finally {
    clearTimeout(timer);
  }
};

// GET /v1/user/get-onboarding-questions  (uses the login token via apiClient)
// Returns the "Data" object: {questions, fuelPrices, users}.
export const getOnboardingQuestions = async () => {
  try {
    const res = await apiClient.get('user/get-onboarding-questions');
    const json = res.data;
    if (json?.Success !== 1 || !json?.Data) {
      throw new Error(json?.Message || 'Failed to load onboarding questions');
    }
    return json.Data;
  } catch (error) {
    throw new Error(
      error.response?.data?.Message ||
        error.response?.data?.message ||
        error.message ||
        'Failed to load onboarding questions',
    );
  }
};

// ---- Stage B: save the answers with the new API ----

// true = ALSO call the old user/answer-questions endpoint after saving. Turn it
// on only if the app says "setup not complete" after saving, which would mean
// save-answers does not set has_answered (ask the backend team).
const ALSO_CALL_LEGACY = false;

// The value sent for each answer. Currently the option code ('weekly',
// 'benzine'). If the backend expects something else, change ONLY this line:
//   opt.label (e.g. 'Every Week') or opt.optionId (the numeric id)
// Accepts either an option object or a plain id string.
const answerValue = opt => (typeof opt === 'string' ? opt : opt?.id);

// POST /v1/user/save-answers  ->  {"answers": {"<questionCode>": <answer>}}
// frequency / fuelType can be the selected option objects OR their id strings.
export const saveOnboardingAnswers = async ({
  frequency,
  fuelType,
  multipleVehicles,
  location,
}) => {
  try {
    const frequencyId = answerValue(frequency);
    const fuelId = answerValue(fuelType);

    if (!frequencyId || !fuelId) {
      throw new Error('Frequency / fuel type missing.');
    }
    // The server wants the question CODES as keys (the API's question codes).
    const answers = {
      fuel_frequency: frequencyId,
      fuel_type: fuelId,
    };
    console.log('[onboarding] save-answers request', JSON.stringify({answers}));
    const res = await apiClient.post('user/save-answers', {answers});
    console.log(
      '[onboarding] save-answers response',
      res.status,
      JSON.stringify(res.data),
    );

    // Some endpoints answer HTTP 200 but put a failure flag in the body.
    const d = res.data;
    if (d?.status === false || d?.success === false || d?.Success === 0) {
      throw new Error(d?.message || d?.Message || 'The server did not accept the answers');
    }

    // keep a copy on the phone so Profile can show the fuel chosen here
    await cacheAnswers({
      frequency: frequencyId,
      fuelType: fuelId,
      multipleVehicles,
      location,
    });

    if (ALSO_CALL_LEGACY) {
      await saveLegacyAnswers({
        frequency: frequencyId,
        fuelType: fuelId,
        multipleVehicles,
        location,
      });
    }
    return d?.data ?? d?.Data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.Message ||
        error.message ||
        'Failed to save answers',
    );
  }
};

// ---- Stage C: save the location with the new API ----

// POST /v1/user/update-location
//   {lat, long, city, country, pincode, metadata: {source, accuracy}}
// `location` is answers.location from the onboarding context:
//   gps:  {mode:'gps', latitude, longitude, accuracy, placeName}
//   city: {mode:'city', city, cityId, latitude?, longitude?}
// Missing coordinates / country / pincode are looked up with Google first.
// If Google fails, the location is still saved with whatever we have.
export const saveOnboardingLocation = async location => {
  if (!location) {
    return null; // the user never reached the location step
  }
  try {
    let latitude = location.latitude ?? null;
    let longitude = location.longitude ?? null;

    // A searched city has no coordinates yet: look them up.
    if (latitude == null && location.cityId) {
      try {
        const withCoords = await getCityCoordinates({id: location.cityId});
        latitude = withCoords.latitude;
        longitude = withCoords.longitude;
      } catch (e) {
        console.log('[onboarding] city coordinates failed:', e?.message);
      }
    }

    // Country and pincode come from reverse geocoding the coordinates.
    let details = {};
    if (latitude != null && longitude != null) {
      try {
        details = (await reverseGeocodeDetails({latitude, longitude})) || {};
      } catch (e) {
        console.log('[onboarding] reverse geocode failed:', e?.message);
      }
    }

    const body = {
      lat: latitude,
      long: longitude,
      city: location.city || location.placeName || details.city || null,
      country: details.country || null,
      pincode: details.pincode || null,
      metadata: {
        source: location.mode, // 'gps' | 'city'
        accuracy: location.accuracy ?? null,
      },
    };
    console.log('[onboarding] update-location request', JSON.stringify(body));
    const res = await apiClient.post('user/update-location', body);
    console.log(
      '[onboarding] update-location response',
      res.status,
      JSON.stringify(res.data),
    );

    const d = res.data;
    if (d?.status === false || d?.success === false || d?.Success === 0) {
      throw new Error(d?.message || d?.Message || 'The server did not accept the location');
    }
    return d?.data ?? d?.Data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.Message ||
        error.message ||
        'Failed to save location',
    );
  }
};

// Saves the user's onboarding answers.
//
// TEMPORARY: this reuses the endpoint the old Question screen used
// ("user/answer-questions"), because that call is what makes the backend mark
// the user as answered (has_answered = true in user/get-profile). Without it the
// user could never get past onboarding.
// The answers are stored in answer_1..answer_5. Those slots used to hold the old
// questions' answers, so ask the backend team for a dedicated endpoint, then
// replace this function.
export const saveLegacyAnswers = async answers => {
  try {
    const userData = await AsyncStorage.getItem('Data');
    const userId = JSON.parse(userData)?.userId;

    const {frequency, fuelType, multipleVehicles, location} = answers;
    const body = {
      userid: userId, // lowercase on purpose: same as the old answerQuestionsService
      answer_1: frequency || '', // how often they refuel, e.g. 'weekly'
      answer_2: fuelType || '', // e.g. 'benzine'
      answer_3: multipleVehicles ? 'yes' : 'no',
      answer_4: location?.mode || '', // 'gps' | 'city'
      answer_5:
        location?.mode === 'gps'
          ? `${location.latitude},${location.longitude}`
          : location?.city || '',
    };

    console.log('[onboarding] saving answers', body);
    const response = await apiClient.post('user/answer-questions', body);
    console.log('[onboarding] save response', response.status, JSON.stringify(response.data));

    // Some endpoints answer HTTP 200 but put a failure flag in the body.
    const d = response.data;
    if (d?.status === false || d?.success === false || d?.Success === 0) {
      throw new Error(d?.message || d?.Message || 'The server did not accept the answers');
    }
    // keep a copy on the phone so Profile can show the fuel chosen here
    try {
      await AsyncStorage.setItem(answersKey(userId), JSON.stringify(body));
    } catch (e) {}
    return d?.data;
  } catch (error) {
    // prefer the server's message; otherwise our own message (e.g. 'Network Error')
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.Message ||
        error.message ||
        'Failed to save answers',
    );
  }
};

// Changes only the fuel type from the Profile screen, using the same
// user/save-answers endpoint as onboarding. `current` is the answers copy
// Profile holds (answer_1..answer_5); it is only used to keep the phone copy
// in sync and to send the frequency back along with the new fuel.
export const updateFuelType = async (fuelType, current = {}) => {
  try {
    const answers = {fuel_type: fuelType};
    // Send the frequency back too, in case the server replaces all answers.
    // Without a copy on this phone (the web app, a new phone) ask the server
    // for it first, so the request is never just {fuel_type}.
    let frequency = current.answer_1;
    if (!frequency) {
      try {
        const profile = await apiClient.get('user/get-profile');
        const data = profile.data?.Data ?? profile.data;
        frequency = data?.answers?.fuel_frequency || data?.answer_1;
      } catch (e) {
        // the change is still sent, without the frequency
      }
    }
    if (frequency) {
      answers.fuel_frequency = frequency;
    }

    console.log('[profile] updating fuel type', JSON.stringify({answers}));
    const res = await apiClient.post('user/save-answers', {answers});
    console.log('[profile] update response', res.status, JSON.stringify(res.data));

    const d = res.data;
    if (d?.status === false || d?.success === false || d?.Success === 0) {
      throw new Error(d?.message || d?.Message || 'The server did not accept the change');
    }

    // keep the phone copy in sync
    try {
      const userData = await AsyncStorage.getItem('Data');
      const userId = JSON.parse(userData)?.userId;
      await AsyncStorage.setItem(
        answersKey(userId),
        JSON.stringify({
          ...current,
          ...(frequency ? {answer_1: frequency} : {}),
          answer_2: fuelType,
        }),
      );
    } catch (e) {}
    return d?.data ?? d?.Data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.Message ||
        error.message ||
        'Failed to update fuel type',
    );
  }
};